import { createRequest, deleteRequest, get, post, put } from '@/api/fetch';
import type { UserData, UserForm } from '@/components/Agency/UserFormData/types';
import type {
  AgencyAgent,
  AgencyData,
  AgencyDetails,
  AgencyProfile,
  AgencyProfileCustomAddress,
  AgencyProfileInformation,
} from '@/types-agency';
import { captureError } from '@smg-real-estate/browser-rum';

import { getPlatformHeader } from '@/helpers/apiUtils';
import store from '@/store';
import { SourcePlatform } from '@/types';
import { UnauthenticatedError } from './UnauthenticatedError';

export interface PropertyOwnerRequest {
  ownerReference: string;
  firstName?: string;
  lastName?: string;
  street?: string;
  postalCode?: string;
  locality?: string;
  country?: string;
  email?: string;
}

export interface PropertyOwnerForBilling extends PropertyOwnerRequest {
  ownerId: string;
}

export async function getAgencies(auth0Id: string): Promise<AgencyDetails[]> {
  const url = `${
    import.meta.env.VUE_APP_ACCOUNT_MANAGEMENT_API_BASE_URL
  }/customer-user-relation/get-customers-for-user/${encodeURIComponent(auth0Id)}`;
  try {
    const response = await get(url);
    return response.json();
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));

    return [];
  }
}

export async function getAgencyUsers(agencyId: string): Promise<UserData[]> {
  const url = `${
    import.meta.env.VUE_APP_ACCOUNT_MANAGEMENT_API_BASE_URL
  }/customer-user-relation/get-users-for-customer/${encodeURIComponent(agencyId)}`;
  try {
    const response = await get(url);
    return (await response.json()).users as UserData[];
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));

    return [];
  }
}

export async function getAgencyProfileByAPI(): Promise<AgencyProfile | undefined> {
  const url = `${import.meta.env.VUE_APP_CUSTOMER_SERVICE_API_BASE_URL}/customers/${store.getters.getActiveAgencyId}/profile`;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to get agency profile with response code: ${response.status}`);
    }
    return (await response.json()).data as AgencyProfile;
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));

    return undefined;
  }
}

export async function getAgencyAgentsByAPI(): Promise<AgencyAgent[]> {
  const url = `${import.meta.env.VUE_APP_CUSTOMER_SERVICE_API_BASE_URL}/customers/${store.getters.getActiveAgencyId}/agents`;
  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to get agency profile with response code: ${response.status}`);
    }

    return (await response.json()).data as AgencyAgent[];
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));

    throw error;
  }
}

export async function getAgencyData(agencyId: string): Promise<AgencyData | undefined> {
  const url = `${import.meta.env.VUE_APP_INSERTION_FUNNEL_API_BASE_URL}/agencyDetails/${agencyId}`;

  try {
    const response = await get(url, undefined, getPlatformHeader(SourcePlatform.SMG));

    if (!response.ok) {
      throw new Error(`Failed to get agency data with response code: ${response.status}`);
    }

    return (await response.json()) as AgencyData;
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));

    return undefined;
  }
}

export interface AgencyProfilePatchPayload {
  descriptions?: string | null;
  services?: string[] | null;
  specializations?: string[] | null;
  logoUrl?: string | null;
  heroBanner?: string | null;
  customAddress?: AgencyProfileCustomAddress | null;
  customPhoneNumber?: string | null;
}

interface AgencyProfilePayload {
  descriptions: string;
  services: string[];
  specializations: string[];
  logoUrl: string;
  heroBanner?: string;
  customAddress?: {
    postalCode: string;
    city: string;
    street: string;
  };
  customPhoneNumber: string;
}

export async function putAgencyProfile(
  agencyId: string,
  payload: AgencyProfilePayload,
  authToken: string,
): Promise<AgencyProfileInformation | undefined> {
  const url = `${import.meta.env.VUE_APP_CUSTOMER_SERVICE_API_BASE_URL}/customers/${agencyId}/profile`;
  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to put agency profile with response code: ${response.status}`);
    }

    return (await response.json()).data as AgencyProfileInformation;
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));

    return undefined;
  }
}

export async function patchAgencyProfile(
  agencyId: string,
  payload: AgencyProfilePatchPayload,
): Promise<AgencyProfileInformation | undefined> {
  const url = `${import.meta.env.VUE_APP_CUSTOMER_SERVICE_API_BASE_URL}/customers/${agencyId}/profile`;
  try {
    const response = await createRequest({ method: 'PATCH', credentials: 'omit' })(
      url,
      JSON.stringify(payload),
    );

    if (response.status === 204) {
      return undefined;
    }

    return (await response.json()).data as AgencyProfileInformation;
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));

    throw error;
  }
}

export async function activateCustomerInvitation(invitationId: string): Promise<boolean> {
  const url = `${import.meta.env.VUE_APP_ACCOUNT_MANAGEMENT_API_BASE_URL}/customer-user-relation/activate-invited-user-for-customer/${invitationId}`;
  try {
    await post(url);
    return true;
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));

    return false;
  }
}

export async function inviteUserForCustomer(payload: UserForm): Promise<boolean> {
  const url = `${import.meta.env.VUE_APP_ACCOUNT_MANAGEMENT_API_BASE_URL}/customer-user-relation/invite-user-for-customers`;
  try {
    const requestBody = JSON.stringify(payload);
    const response = await post(url, requestBody);
    return !!(await response.json()).data?.success?.length;
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));

    return false;
  }
}

export async function getAgencyDetailsById(customerId: string): Promise<AgencyDetails | null> {
  const url = `${import.meta.env.VUE_APP_ACCOUNT_MANAGEMENT_API_BASE_URL}/customers/${customerId}`;
  try {
    const response: Response = await get(url);
    return (await response.json()).data.customer;
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));

    return null;
  }
}

export async function resetPassword(email: string): Promise<any> {
  const url = `${import.meta.env.VUE_APP_ACCOUNT_MANAGEMENT_API_BASE_URL}/request-change-password-email?tenant=ubm`;
  try {
    const requestBody = JSON.stringify({ email: email });
    return await post(url, requestBody);
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));
  }
}

export async function deleteCustomerUserRelation(
  customerId: string,
  userId: string,
): Promise<boolean> {
  const encodedCustomerId = encodeURIComponent(customerId);
  const encodedUserId = encodeURIComponent(userId);
  const url = `${import.meta.env.VUE_APP_ACCOUNT_MANAGEMENT_API_BASE_URL}/customer-user-relation/delete-user-for-customer/${customerId}?customerId=${encodedCustomerId}&userId=${encodedUserId}`;
  try {
    await deleteRequest(url);
    return true;
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));

    return false;
  }
}

export async function checkCustomerAuthorization(): Promise<boolean> {
  const STATUS_OK = 200;
  const url = `${import.meta.env.VUE_APP_ACCOUNT_MANAGEMENT_API_BASE_URL}/customer-user-relation/check-if-user-can-access-agency`;

  try {
    const response = await get(url);

    if (response.status === STATUS_OK) {
      return true;
    } else {
      captureError(new AgencyApiError(url, `Unexpected response code ${response.status}`));

      return false;
    }
  } catch (error) {
    if (!(error instanceof UnauthenticatedError)) {
      captureError(new AgencyApiError(url, `Error in response ${error.statusCode || ''}`));
    }
    return false;
  }
}

export async function getIsBillToAgency(): Promise<boolean> {
  const url = `${import.meta.env.VUE_APP_LISTING_QUALITY_API_BASE_URL}/is-bill-to-agency`;
  try {
    const response: Response = await get(url);

    if (!response.ok) {
      throw new Error(`Failed to get isBillToAgency with response code: ${response.status}`);
    }

    return (await response.json()).isBillToAgency;
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));

    return false;
  }
}

export async function fetchPropertyOwners(): Promise<PropertyOwnerForBilling[]> {
  const url = `${import.meta.env.VUE_APP_LISTING_QUALITY_API_BASE_URL}/property-owners-for-billing`;
  try {
    const response = await get(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch property owners. Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));

    throw error;
  }
}

export async function createPropertyOwner(
  payload: PropertyOwnerRequest,
): Promise<PropertyOwnerForBilling> {
  const url = `${import.meta.env.VUE_APP_LISTING_QUALITY_API_BASE_URL}/property-owners-for-billing`;
  try {
    const response = await post(url, JSON.stringify(payload));

    if (!response.ok) {
      throw new Error(`Failed to create property owner. Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));

    throw error;
  }
}

export const editPropertyOwner = async (
  id: string,
  propertyOwner: PropertyOwnerRequest,
): Promise<PropertyOwnerForBilling> => {
  const url = `${import.meta.env.VUE_APP_LISTING_QUALITY_API_BASE_URL}/property-owners-for-billing/${id}`;
  try {
    const response = await put(url, JSON.stringify(propertyOwner));

    if (!response.ok) {
      throw new Error(`Failed to edit property owner. Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));

    throw error;
  }
};

export const deletePropertyOwner = async (id: string): Promise<void> => {
  const url = `${import.meta.env.VUE_APP_LISTING_QUALITY_API_BASE_URL}/property-owners-for-billing/${id}`;
  try {
    const response = await deleteRequest(url);

    if (!response.ok) {
      throw new Error(`Failed to delete property owner. Status: ${response.status}`);
    }
  } catch (error) {
    captureError(new AgencyApiError(url, error.message));
    throw error;
  }
};

export class AgencyApiError extends Error {
  endpoint: string;

  constructor(endpoint: string, message: string) {
    super(`Unexpected error happened: ${message}`);

    this.name = 'AgencyApiError';
    this.endpoint = endpoint;
  }
}
